import React, {useCallback, useImperativeHandle, useRef, useState, useEffect} from 'react';

import {
  arrow,
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import cn from 'classnames';

import bem from 'client/services/bem';

import {PopoverProps, PopoverRef} from './types';

import {PopoverContent, PopoverArrow} from '../components';

import cssModule from './popover.module.scss';

export const POPOVER_PORTAL_ID = 'portal-root';

const b = bem('popover', {cssModule});

const Popover = React.forwardRef<PopoverRef, PopoverProps>((props, ref) => {
  const {
    trigger = 'hover',
    role: roleSetting = 'tooltip',
    className,
    classNames,
    triggerClassName,
    overlayInnerStyle,
    contentClassName,
    position = 'bottom',
    flipOptions,
    shiftOptions,
    overlay,
    children,
    offset: offsetValue = 8,
    arrowConfig,
    show,
    hideOnMouseLeave,
    disableFocus,
    onOpenChange,
    disabled,
    toggleByClick = true,
    showArrow = true,
    variant = 'light',
    onClose,
    disableAutoUpdatePosition,
    mask,
    maskContainerId,
  } = props;
  const {offset: arrowOffset, ...config} = arrowConfig || {};
  const [isOpen, setIsOpenDirect] = useState(show || false);
  const arrowRef = useRef(null);
  const canCallOnClose = useRef(false);

  useEffect(() => {
    if (!onClose) {
      return;
    }
    if (isOpen && !canCallOnClose.current) {
      canCallOnClose.current = true;
    }
    if (!isOpen && canCallOnClose.current) {
      onClose();
      canCallOnClose.current = false;
    }
  }, [isOpen, onClose]);

  const setIsOpen = useCallback(
    (value: boolean) => {
      if (!disabled) {
        setIsOpenDirect(value);
      }
      onOpenChange?.(value);
    },
    [disabled, onOpenChange],
  );

  const {refs, floatingStyles, context} = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: position,
    middleware: [flip(flipOptions), offset(offsetValue), shift(shiftOptions), arrow({element: arrowRef})],
    whileElementsMounted: !disableAutoUpdatePosition ? autoUpdate : undefined,
  });

  const focus = useFocus(context);
  const hover = useHover(context, {
    enabled: ['both', 'hover'].includes(trigger),
    handleClose: hideOnMouseLeave ? null : safePolygon(),
  });
  const click = useClick(context, {
    enabled: ['both', 'click'].includes(trigger),
    toggle: toggleByClick,
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, {role: roleSetting});
  const headingId = useId();

  const {getReferenceProps, getFloatingProps} = useInteractions([click, hover, dismiss, focus, role]);

  useImperativeHandle(
    ref,
    () => ({
      setOpen: setIsOpen,
    }),
    [setIsOpen],
  );

  return (
    <>
      <div ref={refs.setReference} className={cn(className, triggerClassName, b())} {...getReferenceProps()}>
        {children}
      </div>
      {isOpen && overlay && mask && !!maskContainerId && (
        <FloatingPortal
          preserveTabOrder={false}
          root={maskContainerId ? document.getElementById(maskContainerId) : undefined}
        >
          <div className={b('mask', ['absoulte'])} />
        </FloatingPortal>
      )}
      {isOpen && overlay && (
        <FloatingPortal preserveTabOrder={false} id={POPOVER_PORTAL_ID}>
          {mask && !maskContainerId && <div className={b('mask')} />}
          <FloatingFocusManager
            context={context}
            modal={true}
            closeOnFocusOut={false}
            returnFocus={true}
            initialFocus={-1}
            visuallyHiddenDismiss
            disabled={disableFocus}
          >
            <div
              className={cn(b('overlay-container'), classNames?.overlayContainer)}
              ref={refs.setFloating}
              style={floatingStyles}
              aria-labelledby={headingId}
              {...getFloatingProps()}
            >
              {showArrow && (
                <PopoverArrow
                  ref={arrowRef}
                  context={context}
                  staticOffset={arrowOffset ? `${arrowOffset}%` : null}
                  variant={variant}
                  {...config}
                />
              )}
              <PopoverContent className={contentClassName} style={overlayInnerStyle} variant={variant}>
                {typeof overlay === 'function' ? overlay({isOpen, setIsOpen}) : overlay}
              </PopoverContent>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
});
export default Popover;
